import { AssetFieldsFragment } from '@src/generated/graphql';
import Typography from './Typography';
import { highlightBracketedText } from 'src/utils/utils';
import DynamicLink from './DynamicLink';
import { FaArrowRightLong } from 'react-icons/fa6';
import DynamicMedia from './DynamicMedia';
import { highlightUnderscoreText } from 'src/utils/utils';

type CardProps = {
  item: {
    featureCardTitle: string;
    featureCardDescription: string;
    featureCardImage: { url: string; title: string };
    featureCardLinkText: string;
    featureCardLinkUrl: string;
    card1Title: string;
    card2Title: string;
    card3Title: string;
    card4Title: string;
    card1Description: string;
    card2Description: string;
    card3Description: string;
    card4Description: string;
    card1Image: { url: string };
    card2Image: { url: string };
    card3Image: { url: string };
    card4Image: { url: string };
    videoFile2?: { url: string };
    videoFile3?: { url: string };
  };
  typographyClassName?: string;
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  index?: number;
  tabTitle?: string;
  className?: string;
  buttonText?: string;
  buttonLink?: string;
  // New video-related props
  videoStates?: { [key: string]: { isPlaying: boolean; showVideo: boolean } };
  toggleVideoPlayPause?: (cardIndex: number, position: 2 | 3) => void;
  getMediaSource?: (item: any, cardIndex: number, position: number) => string;
  isVideoUrl?: (url: string) => boolean;
  getVideoUrl?: (item: any, position: 2 | 3) => string;
};

const Card = ({ 
  item, 
  typographyClassName, 
  tag = 'h6', 
  index, 
  tabTitle, 
  className, 
  buttonText, 
  buttonLink,
  videoStates = {},
  toggleVideoPlayPause,
}: CardProps) => {
  const isImageRight = index && (index + 1) % 2 === 0;
  
  // Helper function to check if URL is a video
  const checkIsVideoUrl = (url: string) => {
    return url && (url.includes('.mp4') || url.includes('.webm') || url.includes('.mov'));
  };

  // Helper function to render play/pause button
  const renderPlayPauseButton = (cardIndex: number, position: 2 | 3) => {
    if (!toggleVideoPlayPause) return null;
    
    const videoUrl = position === 2 ? item.videoFile2?.url : item.videoFile3?.url;
    if (!checkIsVideoUrl(videoUrl || '')) return null;

    const stateKey = `${cardIndex}-${position}`;
    const currentState = videoStates[stateKey] || { isPlaying: false, showVideo: false };

    return (
      <button
        onClick={() => toggleVideoPlayPause(cardIndex, position)}
        className="video-detail-play-pause-btn"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.9)',
          border: 'none',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 10
        }}
      >
        {currentState.showVideo && currentState.isPlaying ? (
          // Pause icon - when video is playing
          <svg
            width="20"
            height="20"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V11"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M9 1V11"
              stroke="black"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          // Play icon - when showing thumbnail or video is paused
          <svg
            width="20"
            height="20"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M3 1L10 6L3 11V1Z" fill="black" />
          </svg>
        )}
      </button>
    );
  };

  // Helper function to get media source for each card position
  const getCardMediaSource = (position: number) => {
    const cardIndex = index || 0;
    
    // For positions 2 and 3, check if video exists
    if (position === 2 || position === 3) {
      const videoUrl = position === 2 ? item.videoFile2?.url : item.videoFile3?.url;
      const stateKey = `${cardIndex}-${position}`;
      const currentState = videoStates[stateKey] || { isPlaying: false, showVideo: false };
      
      if (checkIsVideoUrl(videoUrl || '')) {
        // If video exists and we're not showing it yet, use image as thumbnail
        const imageUrl = position === 2 ? item.card2Image?.url : item.card3Image?.url;
        return !currentState.showVideo ? imageUrl : videoUrl;
      }
    }
    
    // Default to respective card image
    switch (position) {
      case 1: return item.card1Image?.url;
      case 2: return item.card2Image?.url;
      case 3: return item.card3Image?.url;
      case 4: return item.card4Image?.url;
      default: return '';
    }
  };

  // Helper function to render media with video support
  const renderMediaWithVideo = (position: number) => {
    const cardIndex = index || 0;
    const mediaSource = getCardMediaSource(position);
    const stateKey = `${cardIndex}-${position}`;
    
    return (
      <div style={{ position: 'relative' }} data-video-id={stateKey}>
        <div className="feature-card__icon">
          <DynamicMedia
            src={mediaSource || ''}
            alt="icon"
            ariaLabel="icon"
            videoAutoplay={false}
            lottieProps={{
              loop: true,
              autoplay: false,
              style: {
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: "unset",
              },
            }}
            lottieClassName="feature-lottie"
          />
        </div>
        
        {/* Only show play/pause button for positions 2 and 3 if they have videos */}
        {(position === 2 || position === 3) && renderPlayPauseButton(cardIndex, position as 2 | 3)}
      </div>
    );
  };

  const cards = [
    { title: item.card1Title, description: item.card1Description, image: item.card1Image, position: 1 },
    { title: item.card2Title, description: item.card2Description, image: item.card2Image, position: 2 },
    { title: item.card3Title, description: item.card3Description, image: item.card3Image, position: 3 },
    { title: item.card4Title, description: item.card4Description, image: item.card4Image, position: 4 },
  ].filter(card => card.title && card.description && card.image);

  return (
    <div className="feature-card">
      <div
        className={`feature-card__header ${isImageRight ? 'feature-card__header--reverse' : ''}`}
      >
        {className?.includes('event-verticaltab') ? (  
          <a 
            href={item.featureCardLinkText || '#'} 
            className="feature-card__image" 
            target="_blank"
          >
            <DynamicMedia
              src={item.featureCardImage?.url}
              alt={item?.featureCardImage.title ?? ''}
              ariaLabel={item?.featureCardImage.title ?? ''}
              lottieProps={{
                loop: true,
              }}
            />
          </a>
        ) : (
          <div className="feature-card__image">
            <DynamicMedia
              src={item.featureCardImage?.url}
              alt={item?.featureCardImage.title ?? ''}
              ariaLabel={item?.featureCardImage.title ?? ''}
              lottieProps={{
                loop: true,
              }}
            />
          </div>
        )}
        <div className="feature-card__header-content">

            {className?.includes('event-verticaltab') && (
             <h3 className='bold blog-heading-3'>Wednesday, August 20, 2025</h3>
            )}
          <span
            className="feature-card__title-main"
            dangerouslySetInnerHTML={{
              __html: highlightBracketedText(item.featureCardTitle ?? '', 'regular'),
            }}
          />
          {item.featureCardDescription && className?.includes('event-verticaltab') ? (
            <div className="feature-card__description-main">
              <strong>Schedule:</strong>
              <ul style={{ marginLeft: 10, paddingLeft: 20 }}>
                {item.featureCardDescription
                  .split(/\\n|\/n|\n/g)
                  .map(line => line.trim())
                  .filter(line => line)
                  .map((line, idx) => (
                    <li key={idx}>{line.replace(/^[-*•]\s*/, '')}</li>
                  ))}
              </ul>
            </div>
          ) : item.featureCardDescription && (
            <p
              className="feature-card__description-main"
              dangerouslySetInnerHTML={{
                __html: highlightUnderscoreText(
                  (item.featureCardDescription ?? '')
                    .replace(/\\n/g, '<br/>')
                    .replace(/\/n/g, '<br/>')
                    .replace(/\n/g, '<br/>'),
                ),
              }}
            />
          )}

          {className?.includes('event-verticaltab') && buttonText?.trim() && (
            <div>
              <DynamicLink url={buttonLink} className="btn-default-bdr">
                <span>{buttonText}</span>
                <FaArrowRightLong className="btn-icon" />
              </DynamicLink>
            </div>
          )}
          {item.featureCardLinkText?.trim() && (
            <div>
              <DynamicLink url={item.featureCardLinkUrl} className="btn-default-bdr hide-event">
                <span>{item.featureCardLinkText}</span>
                <FaArrowRightLong className="btn-icon" />
              </DynamicLink>
            </div>
            )}
        </div>
      </div>

      <hr className="feature-card__divider" />
      {className?.includes('event-verticaltab') && tabTitle && tabTitle.trim() && (
        <div className="feature-card__tab-title" style={{ marginBottom: '24px' }}>
          <h3 style={{ fontWeight: 700, fontSize: '24px', margin: 0 }}>{tabTitle}</h3>
        </div>
      )}
      <div className="feature-card__grid feature-panel-grid">
        {cards.map((card, idx) => (
          <div key={idx} className="feature-card__item">
            {renderMediaWithVideo(card.position)}
            <div className="feature-card__content">
              <Typography tag={tag} className={`feature-card__title ${typographyClassName || ''}`}>
                {card.title}
              </Typography>
              <p className="feature-card__description">{card.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Card;